import { Connection, VersionedTransaction } from "@solana/web3.js";

import { DEBUG } from "../../constants";

export interface DebugAccountInfo {
    index: number;
    address: string;
    link: string;
}

export interface LUTDebugInfo extends DebugAccountInfo {
    alt: string;
}

/**
 * Collects debug information for a transaction including lookup table and static account details
 */
export async function logTransactionDebugInfo(
    tx: VersionedTransaction,
    connection: Connection,
    simulationLogs?: string[] | undefined
): Promise<void> {
    if (!DEBUG) return;
    const lookupTableToAddresses = new Map<string, string[]>();
    const lutRows: LUTDebugInfo[] = [];
    const staticRows: DebugAccountInfo[] = [];

    const msg = tx.message;

    // Collect static account keys
    (msg.staticAccountKeys ?? []).forEach((k, i) => {
        const addr = k.toBase58();
        staticRows.push({ index: i, address: addr, link: `https://solscan.io/account/${addr}` });
    });

    // Collect lookup table information
    for (const l of msg.addressTableLookups ?? []) {
        const altKey = l.accountKey;
        const alt = altKey.toBase58();
        let addrs = lookupTableToAddresses.get(alt);
        if (!addrs) {
            const { value } = await connection.getAddressLookupTable(altKey);
            addrs = (value?.state.addresses ?? []).map((a) => a.toBase58());
            // ensure non-undefined going forward
            addrs = addrs ?? [];
            lookupTableToAddresses.set(alt, addrs);
        }

        const addresses = addrs;
        const pushIdxs = (idxs: number[]) => {
            idxs.forEach((i) => {
                const address = addresses[i] ?? "<out-of-bounds>";
                lutRows.push({ alt, index: i, address, link: `https://solscan.io/account/${address}` });
            });
        };

        pushIdxs(l.writableIndexes ?? []);
        pushIdxs(l.readonlyIndexes ?? []);
    }

    if (lutRows.length > 0) {
        // eslint-disable-next-line no-console
        console.groupCollapsed("DEBUG: LUT Accounts");
        // eslint-disable-next-line no-console
        console.table(lutRows);
        // eslint-disable-next-line no-console
        console.groupEnd();
    }

    if (staticRows.length > 0) {
        // eslint-disable-next-line no-console
        console.groupCollapsed("DEBUG: Static Accounts (not in LUTs)");
        // eslint-disable-next-line no-console
        console.table(staticRows);
        // eslint-disable-next-line no-console
        console.groupEnd();
    }
    if (simulationLogs) {
        // eslint-disable-next-line no-console
        console.log("Simulation logs:", simulationLogs);
    }
}
